import {
  useRef,
  useState,
  type MouseEventHandler,
  type PropsWithChildren,
} from 'react';

interface Position { x: number; y: number; }

interface SpotlightCardProps extends PropsWithChildren {
  className?: string;
  spotlightColor?: `rgba(${number}, ${number}, ${number}, ${number})`;
}

export function SpotlightCard({
  children,
  className = '',
  spotlightColor = 'rgba(251, 171, 44, 0.22)',
}: SpotlightCardProps): JSX.Element {
  const divRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState<number>(0);

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
    if (divRef.current === null || isFocused) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={(): void => { setIsFocused(true); setOpacity(0.6); }}
      onBlur={(): void => { setIsFocused(false); setOpacity(0); }}
      onMouseEnter={(): void => setOpacity(0.6)}
      onMouseLeave={(): void => setOpacity(0)}
      className={`group relative rounded-3xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl overflow-hidden p-7 transition-colors hover:border-white/[0.16] ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-in-out"
        style={{
          opacity,
          background: `radial-gradient(circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 60%)`,
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
